import type { AppToBuildStr } from "../../../appsConfig";
import {
  MAX_ORG_SEAT_COUNT,
  ORG_EXAM_TYPES,
  orgPurchasesStrings,
  type OrgExamType,
} from "@petergok/blue-collar-shared";

export interface OrgSeatPrice {
  /** ISO currency code, lowercase (e.g. "usd", "cad") — as Stripe returns it. */
  currency: string;
  /** Price per seat in the currency's smallest unit (cents). */
  unitAmount: number;
}

// UX clamp for the on-page seat picker. The backend remains authoritative.
export const MAX_ORG_SEATS = MAX_ORG_SEAT_COUNT;

// Per-exam fallback prices used only when the live Stripe price fetch fails.
// Mirrors each app's standard annual subscription default price in Stripe
// (verified live 2026-06-02); keep in sync if Stripe pricing changes. Amount is
// in the currency's smallest unit (cents).
export const ORG_SEAT_FALLBACK_PRICES: Partial<Record<OrgExamType, OrgSeatPrice>> = {
  nec: { currency: "usd", unitAmount: 9999 }, // $99.99 / yr
  cec: { currency: "cad", unitAmount: 7999 }, // CA$79.99 / yr
  plumbing: { currency: "cad", unitAmount: 7999 }, // CA$79.99 / yr
};

export const ORG_PURCHASE_EXAMS = [
  {
    examType: "nec",
    label: orgPurchasesStrings.en.examLabels.nec,
    title: "NEC Electrical Exam Prep",
    description: "Give apprentices one year of Dakota Prep Pro for the NEC track.",
    proAccessBlurb:
      "Get access to 3000+ exam questions, AI tutor, written and video study guides to help pass your journeyman, master or contractor exam. Applicable for all 50 states.",
  },
  {
    examType: "cec",
    label: orgPurchasesStrings.en.examLabels.cec,
    title: "Canadian Electrical Exam Prep",
    description: "Sponsor Red Seal electrical learners on the Canadian exam track.",
    proAccessBlurb:
      "Get access to 2500+ exam questions, AI tutor, written and video study guides to help pass your Red Seal licensing exam. Covers Red Seal (IP/CofQ) for 309A and 442A, plus Level 1 through 4 apprentice exams.",
  },
  {
    examType: "plumbing",
    label: orgPurchasesStrings.en.examLabels.plumbing,
    title: "Canadian Plumbing Exam Prep",
    description: "Help plumbing apprentices prepare with a full year of Pro access.",
    proAccessBlurb:
      "Get access to 1000+ exam questions, AI tutor, written study guides to help pass your Red Seal licensing exam. Covers Red Seal for Plumbers.",
  },
] as const satisfies ReadonlyArray<{
  examType: OrgExamType;
  label: string;
  title: string;
  description: string;
  proAccessBlurb: string;
}>;

export type OrgPurchaseExamType = (typeof ORG_PURCHASE_EXAMS)[number]["examType"];

export function isOrgExamType(value: unknown): value is OrgExamType {
  return typeof value === "string" && ORG_EXAM_TYPES.includes(value as OrgExamType);
}

export function getOrgExamConfig(examType: OrgPurchaseExamType) {
  const config = ORG_PURCHASE_EXAMS.find((exam) => exam.examType === examType);
  if (!config) {
    throw new Error(`Missing ORG_PURCHASE_EXAMS entry for examType "${examType}"`);
  }
  return config;
}

export function getOrgExamTypeForApp(app: AppToBuildStr): OrgExamType | null {
  switch (app) {
    case "electrical_us":
      return "nec";
    case "electrical":
      return "cec";
    case "plumbing":
    case "plumbing_us":
      return "plumbing";
    default:
      return null;
  }
}

export function getRouterPrefix(pathname = window.location.pathname) {
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  return firstSegment &&
    ["electrical", "electrical_us", "plumbing", "ibew_aptitude"].includes(firstSegment)
    ? `/${firstSegment}`
    : "";
}

export function buildOrgLandingUrl(examType: OrgExamType) {
  return `${window.location.origin}${getRouterPrefix()}/organizations/${examType}`;
}
